import { assertNever, type SimEvent } from "@reagent/sim";
import type { CueName } from "./cues";

/**
 * Translates simulation events into sound, and is exhaustive over SimEvent for
 * the same reason `fxForEvent` is: adding an event to the protocol without
 * deciding what it sounds like is a compile error here.
 *
 * That symmetry is deliberate. The visual and audio registries are two
 * independent readings of the same event stream, so an event cannot pick up a
 * picture and quietly stay silent — a hazard the player can see but not hear
 * is one they will miss while looking at their hand.
 *
 * Returning `null` is a legitimate decision, but it has to be an explicit one.
 */
export function cueForEvent(event: SimEvent): CueName | null {
  switch (event.type) {
    case "packet.moved":
      return "packetMove";

    case "packet.merged":
      return "merge";

    case "packet.phaseConverted":
      return "phaseConvert";

    case "packet.separated":
      return "separate";

    case "reaction.fired":
      // The one event whose sound depends on its payload: an exotherm is the
      // thing that kills you (`03-hazards.md` §2.1), so it gets the hiss and the endotherm
      // gets a falling tone. Same picture, different warning.
      return event.isExothermic ? "reactionExothermic" : "reactionEndothermic";

    case "node.heated":
      // Heat ticks every turn on a working rig, so only the back half of the
      // climb makes a sound — matching the FX registry's threshold, so the
      // shimmer and the rising tone arrive together.
      return event.heatCounter / Math.max(event.criticalTemp, 1) < 0.5 ? null : "heat";

    case "node.overheatWarning":
      return "overheatWarning";

    case "node.exploded":
      return "explode";

    case "pipe.pressured":
      return "pressure";

    case "pipe.burst":
      return "burst";

    case "pipe.corroded":
      return "corrode";

    case "pipe.leaking":
      return "leak";

    case "catalyst.poisoned":
      return "poison";

    case "packet.sludged":
      return "sludge";

    case "node.regulated":
      return "regulate";

    case "node.valveSet":
      // A player toggled a flowMover's routing setting. Reuse switchThrow — a
      // literal mechanical toggle, a perfect fit for "a valve was thrown".
      return "switchThrow";

    case "feed.exhausted":
      // Supply line ran dry: machinery failure, same as infested's
      // "dead machinery" register. Not electrical (so not powerOutage).
      return "infested";

    case "node.vented":
      // A vent flares waste (spec 0032): the disposal mechanism bursts the material
      // out of the system. Reuse burst — the same audio vocabulary as pipe.burst,
      // but this is an intentional, designed action rather than failure.
      return "burst";

    case "card.played":
      return "cardPlay";

    case "objective.progressed":
      return "objective";

    case "deck.polluted":
      return "polluted";

    case "tile.contaminated":
      return "contaminated";

    case "facility.verminRisk":
      return "verminRisk";

    case "node.infested":
      return "infested";

    case "node.infestationCleared":
      // The infestation ended (starved or fumigated). Reuse the hazard-cleared cue
      // to convey relief at the problem being resolved.
      return "hazardCleared";

    case "facility.powerOutage":
      return "powerOutage";

    case "facility.disaster":
      return "structuralDisaster";

    case "hazard.cardPenalty":
      return "hazardPenalty";

    case "hazard.cardCleared":
      return "hazardCleared";

    case "hardware.installed":
      return "hardwareInstalled";

    case "hardware.dismantled":
      return "hardwareDismantled";

    case "hardware.moved":
      // Hardware snaps into a new location mid-Play. Reuse hardwareInstalled: both
      // are placements where hardware catches and settles into place, just at
      // different phases.
      return "hardwareInstalled";

    case "hardware.rebound":
      // Node recipe rebinding during Play. Reuse hardwareInstalled: a configuration
      // snap settling into place, same vocabulary as hardware.moved and
      // hardware.installed even though the node's position doesn't change.
      return "hardwareInstalled";

    case "tile.cleaned":
      // A contaminated tile just cleared (fee or directive). Reuse hazardCleared:
      // both are relief moments where a hazard's threat has been removed.
      return "hazardCleared";

    case "run.cleared":
      return "runCleared";

    case "run.failed":
      return "runFailed";

    // Phase changes are announced by the switch the player just threw and by
    // the hand that arrives; a sound of their own would double every one of
    // them.
    case "phase.changed":
      return null;

    default:
      return assertNever(event, "cueForEvent");
  }
}
